import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, Plus, Trash2, Printer, Calendar, Pencil, BookmarkCheck } from 'lucide-react';
import { toast } from 'sonner';
import ShiftAlerts from '../components/ShiftAlerts';
import { useStore } from '../context/StoreContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STORE_KEYS = ['orewa', 'eastridge'];

// Each store has its own fortnight anchor (Monday of any known fortnight start).
// Defaults can be overridden per-store via localStorage `roster_anchor_<storeKey>`.
const DEFAULT_ANCHORS = {
  orewa: '2026-06-01',     // Mon 1 Jun 2026 (user's confirmed Orewa pay cycle)
  eastridge: '2026-06-01', // same default; user can change via the "Set anchor" date picker
};

const getStoreAnchor = (storeKey) => {
  try {
    const stored = localStorage.getItem(`roster_anchor_${storeKey}`);
    if (stored && /^\d{4}-\d{2}-\d{2}$/.test(stored)) return stored;
  } catch (_e) { /* ignore */ }
  return DEFAULT_ANCHORS[storeKey] || DEFAULT_ANCHORS.orewa;
};

const setStoreAnchor = (storeKey, isoDateStr) => {
  try { localStorage.setItem(`roster_anchor_${storeKey}`, isoDateStr); } catch (_e) { /* ignore */ }
};

// Pay-cycle anchors are stored per-store. Anchors are any known fortnight Monday;
// every fortnight is 14 days from that anchor. Payday = anchor + 18 days (Friday after the Sunday end).
const FORTNIGHT_ANCHOR_FALLBACK = new Date(2026, 5, 1); // 1 Jun 2026 (Mon)

const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

function getMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
// Align a date to the start of its fortnight (Monday) based on the given anchor (ISO string).
function getFortnightStart(date, anchorIso) {
  const anchor = anchorIso
    ? new Date(anchorIso + 'T00:00:00')
    : FORTNIGHT_ANCHOR_FALLBACK;
  const monday = getMonday(date);
  const msPerDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.round((monday - anchor) / msPerDay);
  const mod = ((diffDays % 14) + 14) % 14;
  return addDays(monday, -mod);
}
const fmtDayLabel = (monday, dayIdx) => addDays(monday, dayIdx).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' });
const fmtFullDate = (d) => d.toLocaleDateString('en-NZ', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

function calcHours(start, end, breakMin) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  return Math.max(0, Math.round(((mins - (breakMin || 0)) / 60) * 100) / 100);
}

// Parse user-typed time string into "HH:MM" 24h format.
// Accepts: "9", "9:30", "930", "9.30", "9am", "9:30 pm", "21:00", "6:30PM" etc.
// `fieldContext`: 'start' or 'end' — used to guess am/pm for ambiguous entries.
// `pairedTime`: the other field's already-set time (e.g. start when parsing end), used as a hint.
function parseTimeInput(raw, fieldContext = null, pairedTime = '') {
  if (!raw) return '';
  const s = String(raw).trim().toLowerCase().replace(/\s/g, '');
  if (!s) return '';
  let ampm = null;
  let body = s;
  if (s.endsWith('am')) { ampm = 'am'; body = s.slice(0, -2); }
  else if (s.endsWith('pm')) { ampm = 'pm'; body = s.slice(0, -2); }
  else if (s.endsWith('a')) { ampm = 'am'; body = s.slice(0, -1); }
  else if (s.endsWith('p')) { ampm = 'pm'; body = s.slice(0, -1); }
  body = body.replace('.', ':');
  let hh = 0, mm = 0;
  if (body.includes(':')) {
    const [h, m] = body.split(':');
    hh = parseInt(h, 10);
    mm = parseInt(m || '0', 10);
  } else if (body.length === 4) {
    hh = parseInt(body.slice(0, 2), 10);
    mm = parseInt(body.slice(2), 10);
  } else if (body.length === 3) {
    hh = parseInt(body.slice(0, 1), 10);
    mm = parseInt(body.slice(1), 10);
  } else {
    hh = parseInt(body, 10);
    mm = 0;
  }
  if (isNaN(hh) || isNaN(mm)) return '';

  // Smart am/pm guess for ambiguous entries (no explicit am/pm, hh < 13)
  if (ampm === null && hh < 13 && hh > 0) {
    if (fieldContext === 'end' && pairedTime) {
      // Guess based on start time — if end_hh would be < start_hh in 24h, assume pm
      const [sh] = pairedTime.split(':').map(Number);
      if (!isNaN(sh) && hh < sh) {
        ampm = 'pm';
      }
    } else if (fieldContext === 'end' && hh <= 8) {
      // Common retail shifts end PM. "6:30" alone for end probably = 6:30pm
      ampm = 'pm';
    } else if (fieldContext === 'start' && hh < 7) {
      // Early start <7 with no am/pm — assume am unless... typically retail opens 8-10am
      ampm = 'am';
    }
  }

  if (ampm === 'pm' && hh < 12) hh += 12;
  if (ampm === 'am' && hh === 12) hh = 0;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return '';
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

// Display 24h "HH:MM" as friendly "9:30am" / "6:30pm"
function fmtTimeDisplay(hhmm) {
  if (!hhmm || !hhmm.includes(':')) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h)) return '';
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}

function WeekTable({ weekStart, staffRows, setStaffRows, knownNames, refresh, getAuthHeader, label, fortnightView }) {
  const monday = new Date(weekStart + 'T00:00:00');
  const [saving, setSaving] = useState({});
  // Local "draft" text for each cell's start/end so user can freely type
  const [drafts, setDrafts] = useState({});
  const weekLabel = `${monday.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' })} – ${addDays(monday, 6).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' })}`;
  const weekTotal = Math.round(staffRows.reduce((s, r) => s + (r.total_hours || 0), 0) * 100) / 100;

  const draftKey = (staffName, dayIndex, field) => `${staffName}-${dayIndex}-${field}`;

  const getDisplayValue = (staffName, dayIndex, field) => {
    const k = draftKey(staffName, dayIndex, field);
    if (k in drafts) return drafts[k];
    // Show the saved canonical value as a friendly display
    const row = staffRows.find(s => s.staff_name === staffName);
    const day = row?.days?.[dayIndex] || row?.days?.[String(dayIndex)];
    return day && day[field] ? fmtTimeDisplay(day[field]) : '';
  };

  const onDraftChange = (staffName, dayIndex, field, raw) => {
    setDrafts(prev => ({ ...prev, [draftKey(staffName, dayIndex, field)]: raw }));
  };

  const commitTime = (staffName, dayIndex, field) => {
    const k = draftKey(staffName, dayIndex, field);
    const raw = drafts[k];
    if (raw === undefined) return; // nothing typed
    const row = staffRows.find(s => s.staff_name === staffName);
    if (!row) return;
    const cur = row.days?.[dayIndex] || row.days?.[String(dayIndex)] || { start_time: '', end_time: '', break_minutes: 0 };
    // Smart parse — pass field context + paired time hint for am/pm guessing
    const otherField = field === 'start_time' ? 'end_time' : 'start_time';
    const ctx = field === 'start_time' ? 'start' : 'end';
    const parsed = parseTimeInput(raw, ctx, cur[otherField] || '');
    const newDay = { ...cur, [field]: parsed };
    newDay.hours = calcHours(newDay.start_time || '', newDay.end_time || '', newDay.break_minutes || 0);

    // Update local state
    setStaffRows(prev => prev.map(s => {
      if (s.staff_name !== staffName) return s;
      const days = { ...s.days };
      days[dayIndex] = newDay;
      const total = Object.values(days).reduce((sum, d) => sum + (d.hours || 0), 0);
      return { ...s, days, total_hours: Math.round(total * 100) / 100 };
    }));
    // Clear the draft so display reverts to canonical fmtTimeDisplay
    setDrafts(prev => { const c = { ...prev }; delete c[k]; return c; });
    // Persist — pass the new day values DIRECTLY so we don't hit a stale state closure
    persistCell(staffName, dayIndex, newDay, row.store);
  };

  const persistCell = async (staffName, dayIndex, day, store) => {
    const key = `${weekStart}-${staffName}-${dayIndex}`;
    setSaving(p => ({ ...p, [key]: true }));
    try {
      await axios.post(`${API_URL}/staff/weekly-hours`, {
        staff_name: staffName,
        week_start: weekStart,
        day_index: parseInt(dayIndex),
        start_time: day.start_time || '',
        end_time: day.end_time || '',
        break_minutes: day.break_minutes || 0,
        notes: day.notes || '',
        store: store,
      }, { headers: getAuthHeader() });
      // Mark this cell as "just saved" — UI shows a green tick for 2 seconds
      setSaving(p => ({ ...p, [key]: 'saved' }));
      setTimeout(() => setSaving(p => { const n = { ...p }; if (n[key] === 'saved') delete n[key]; return n; }), 2000);
    } catch (e) {
      // Loud failure — sticky red toast so the user can't miss it
      toast.error(`⚠️ ROSTER SAVE FAILED for ${staffName} (day ${dayIndex}): ${e.response?.data?.detail || e.message}. Please retry or screenshot this.`, { duration: 10000 });
      setSaving(p => ({ ...p, [key]: 'failed' }));
    }
  };

  const updateCell = (staffName, dayIndex, field, value) => {
    setStaffRows(prev => prev.map(s => {
      if (s.staff_name !== staffName) return s;
      const days = { ...s.days };
      const cur = days[dayIndex] || days[String(dayIndex)] || { start_time: '', end_time: '', break_minutes: 0, hours: 0 };
      days[dayIndex] = { ...cur, [field]: value };
      const { start_time, end_time, break_minutes } = days[dayIndex];
      days[dayIndex].hours = calcHours(start_time, end_time, break_minutes);
      const total = Object.values(days).reduce((sum, d) => sum + (d.hours || 0), 0);
      return { ...s, days, total_hours: Math.round(total * 100) / 100 };
    }));
  };

  const saveCell = async (staffName, dayIndex) => {
    const row = staffRows.find(s => s.staff_name === staffName);
    if (!row) return;
    const day = row.days[dayIndex];
    const key = `${weekStart}-${staffName}-${dayIndex}`;
    setSaving(p => ({ ...p, [key]: true }));
    try {
      await axios.post(`${API_URL}/staff/weekly-hours`, {
        staff_name: staffName, week_start: weekStart, day_index: parseInt(dayIndex),
        start_time: day.start_time || '', end_time: day.end_time || '',
        break_minutes: day.break_minutes || 0, notes: day.notes || '', store: row.store,
      }, { headers: getAuthHeader() });
      setSaving(p => ({ ...p, [key]: 'saved' }));
      setTimeout(() => setSaving(p => { const n = { ...p }; if (n[key] === 'saved') delete n[key]; return n; }), 2000);
    } catch (e) {
      toast.error(`⚠️ ROSTER SAVE FAILED for ${staffName} (day ${dayIndex}): ${e.response?.data?.detail || e.message}. Please retry or screenshot this.`, { duration: 10000 });
      setSaving(p => ({ ...p, [key]: 'failed' }));
    }
  };

  const updateWeekNotes = (staffName, notes) => {
    setStaffRows(prev => prev.map(s => s.staff_name === staffName ? { ...s, week_notes: notes } : s));
  };

  const saveWeekNotes = async (staffName) => {
    const row = staffRows.find(s => s.staff_name === staffName);
    if (!row) return;
    try {
      await axios.post(`${API_URL}/staff/weekly-notes`, {
        staff_name: staffName, week_start: weekStart, notes: row.week_notes || '', store: row.store,
      }, { headers: getAuthHeader() });
    } catch (e) {
      toast.error('Notes save failed');
    }
  };

  const removeStaff = async (name) => {
    // Two-step confirm: type the staff name exactly to confirm. Prevents the
    // accidental click that could wipe a whole week of hours.
    const typed = window.prompt(
      `⚠️ REMOVE ${name} from this week's roster?\n\n` +
      `This DELETES all of ${name}'s hours for week starting ${weekStart}. ` +
      `This cannot be undone from the UI (you'd have to retype them).\n\n` +
      `If you're sure, type the staff name exactly to confirm:\n\n${name}`
    );
    if (typed === null) return;  // Cancel
    if (typed.trim() !== name) {
      toast.error(`Name didn't match (you typed "${typed}"). Nothing removed.`);
      return;
    }
    try {
      await axios.delete(`${API_URL}/staff/weekly-hours/staff?staff_name=${encodeURIComponent(name)}&week_start=${weekStart}&store=${encodeURIComponent(staffRows.find(s => s.staff_name === name)?.store || '')}`, { headers: getAuthHeader() });
      toast.success(`Removed ${name} from week of ${weekStart}`);
      refresh();
    } catch (e) { toast.error('Remove failed — your data is still there. Please screenshot the page and contact support.'); }
  };

  const saveAsDefault = async (name) => {
    // Lock in this staff's current-week hours as their permanent default so
    // every future add-staff call auto-fills the SAME pattern. User can
    // override the default anytime by clicking Save as Default again.
    const row = staffRows.find(s => s.staff_name === name);
    if (!row) return;
    const totalHours = Object.values(row.days || {}).reduce((s, d) => s + (d.hours || 0), 0);
    const workingDays = Object.values(row.days || {}).filter(d => d.hours > 0).length;
    const confirmed = window.confirm(
      `Save as ${name}'s default schedule?\n\n` +
      `${workingDays} working day${workingDays === 1 ? '' : 's'} · ${totalHours}h/week\n\n` +
      `From now on, whenever you add ${name} to a new week, these hours will auto-fill.`
    );
    if (!confirmed) return;
    try {
      // Build days dict from the current-week row
      const daysPayload = {};
      for (let i = 0; i < 7; i++) {
        const d = row.days?.[i] || row.days?.[String(i)] || {};
        daysPayload[String(i)] = {
          start_time: d.start_time || '',
          end_time: d.end_time || '',
          break_minutes: d.break_minutes || 0,
          hours: d.hours || 0,
        };
      }
      await axios.post(
        `${API_URL}/staff/default-hours`,
        { staff_name: name, days: daysPayload },
        { headers: getAuthHeader() },
      );
      toast.success(`${name}'s default saved (${totalHours}h/week). Future weeks will auto-fill.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save default');
    }
  };

  const renameStaff = async (oldName) => {
    const newName = window.prompt(
      `Rename "${oldName}" to what?\n\n` +
      `This renames ${oldName} across ALL weeks (preserves their hours, notes & history). ` +
      `Useful if you typed a name wrong, or one staff member is replacing another.`,
      oldName,
    );
    if (newName === null) return;  // Cancel
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error('Name cannot be empty');
      return;
    }
    if (trimmed === oldName) return;  // No change
    try {
      const res = await axios.post(
        `${API_URL}/staff/weekly-hours/rename-staff`,
        { old_name: oldName, new_name: trimmed, week_start: weekStart, scope: 'all_weeks' },
        { headers: getAuthHeader() },
      );
      toast.success(`Renamed ${oldName} → ${trimmed} (${res.data.weeks_affected || 0} weeks updated)`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rename failed');
    }
  };

  // Derived save status — visible banner so the user always knows whether
  // their changes have actually persisted. "saved" cells fade after 2s; "failed"
  // cells stick around until the user retries.
  const savePending = Object.values(saving).filter(v => v === true).length;
  const saveFailed = Object.values(saving).filter(v => v === 'failed').length;
  const saveJust = Object.values(saving).filter(v => v === 'saved').length;
  const statusBanner = saveFailed > 0 ? (
    <div className="px-3 py-1.5 rounded text-sm font-bold bg-red-100 text-red-900 border border-red-400" data-testid="roster-save-status-failed">
      ⚠️ {saveFailed} save{saveFailed === 1 ? '' : 's'} FAILED — your data didn't save. Please retype or screenshot & contact support.
    </div>
  ) : savePending > 0 ? (
    <div className="px-3 py-1.5 rounded text-sm font-medium bg-amber-50 text-amber-900 border border-amber-300" data-testid="roster-save-status-saving">
      💾 Saving {savePending} change{savePending === 1 ? '' : 's'}…
    </div>
  ) : saveJust > 0 ? (
    <div className="px-3 py-1.5 rounded text-sm font-medium bg-emerald-50 text-emerald-900 border border-emerald-300" data-testid="roster-save-status-saved">
      ✓ Saved
    </div>
  ) : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg flex items-center gap-3" data-testid={`week-label-${label}`}>
            {fortnightView && <Badge className="bg-[#1b5e20] text-white">Week {label}</Badge>}
            <span>{weekLabel}</span>
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            {statusBanner}
            <Badge variant="outline" className="text-base font-bold bg-emerald-50 text-emerald-900 border-emerald-300 px-3 py-1" data-testid={`week-total-${label}`}>
              Week total: {weekTotal} hrs
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        {staffRows.length === 0 ? (
          <div className="text-center py-8 text-slate-500">No staff added yet.</div>
        ) : (
          <table className="w-full border-collapse text-sm" data-testid={`roster-table-${label}`}>
            <thead>
              <tr className="border-b-2 border-slate-200 bg-slate-50">
                <th className="text-left py-2 px-3 font-semibold text-slate-700 min-w-[90px] sticky left-0 bg-slate-50">Day</th>
                {staffRows.map(row => (
                  <th key={row.staff_name} className="text-center py-2 px-2 font-semibold text-slate-700 min-w-[160px]" data-testid={`col-header-${label}-${row.staff_name}`}>
                    <span className="inline-flex items-center gap-1">
                      {row.staff_name}
                      <button
                        onClick={() => renameStaff(row.staff_name)}
                        className="text-slate-300 hover:text-blue-600 align-middle"
                        title={`Rename ${row.staff_name} (preserves all hours & history)`}
                        data-testid={`rename-staff-${label}-${row.staff_name}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => saveAsDefault(row.staff_name)}
                        className="text-slate-300 hover:text-emerald-600 align-middle"
                        title={`Save as ${row.staff_name}'s default schedule — future weeks will auto-fill these hours`}
                        data-testid={`save-default-${label}-${row.staff_name}`}
                      >
                        <BookmarkCheck className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeStaff(row.staff_name)}
                        className="text-slate-300 hover:text-red-500 align-middle"
                        title="Remove from this week"
                        data-testid={`remove-staff-${label}-${row.staff_name}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAY_LABELS.map((dayLabel, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/40" data-testid={`day-row-${label}-${i}`}>
                  <td className="py-2 px-3 font-semibold text-slate-700 align-top sticky left-0 bg-white">
                    <div>{dayLabel}</div>
                    <div className="text-[10px] font-normal text-slate-500">{fmtDayLabel(monday, i)}</div>
                  </td>
                  {staffRows.map(row => {
                    const day = row.days?.[i] || row.days?.[String(i)] || { start_time: '', end_time: '', break_minutes: 0, hours: 0 };
                    return (
                      <td key={row.staff_name} className="py-1 px-1 align-top" data-testid={`row-${label}-${row.staff_name}`}>
                        <div className="flex items-center gap-1">
                          <Input type="text" value={getDisplayValue(row.staff_name, i, 'start_time')}
                            onChange={(e) => onDraftChange(row.staff_name, i, 'start_time', e.target.value)}
                            onBlur={() => commitTime(row.staff_name, i, 'start_time')}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            placeholder="9am"
                            className="h-7 text-xs px-1 w-[68px]"
                            data-testid={`start-${label}-${row.staff_name}-${i}`}/>
                          <span className="text-slate-300">–</span>
                          <Input type="text" value={getDisplayValue(row.staff_name, i, 'end_time')}
                            onChange={(e) => onDraftChange(row.staff_name, i, 'end_time', e.target.value)}
                            onBlur={() => commitTime(row.staff_name, i, 'end_time')}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            placeholder="6pm"
                            className="h-7 text-xs px-1 w-[68px]"
                            data-testid={`end-${label}-${row.staff_name}-${i}`}/>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <Input type="number" min="0" step="5" placeholder="break"
                            value={day.break_minutes || ''}
                            onChange={(e) => updateCell(row.staff_name, i, 'break_minutes', parseInt(e.target.value) || 0)}
                            onBlur={() => {
                              const fresh = staffRows.find(s => s.staff_name === row.staff_name);
                              const freshDay = fresh?.days?.[i] || fresh?.days?.[String(i)] || day;
                              persistCell(row.staff_name, i, freshDay, row.store);
                            }}
                            className="h-6 text-[10px] px-1 w-[44px]"
                            title="break minutes"/>
                          <span className="text-[10px] text-slate-400">min</span>
                          <span className={`ml-auto px-1.5 py-0.5 rounded text-sm font-extrabold ${day.hours > 0 ? 'bg-emerald-100 text-emerald-800' : 'text-slate-300'}`} data-testid={`hours-${label}-${row.staff_name}-${i}`}>
                            {day.hours > 0 ? `${day.hours}h` : '—'}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {/* TOTAL row */}
              <tr className="border-t-2 border-emerald-300 bg-emerald-50">
                <td className="py-2 px-3 font-bold text-emerald-900 sticky left-0 bg-emerald-50">Week total</td>
                {staffRows.map(row => (
                  <td key={row.staff_name} className="py-2 px-2 text-center text-lg font-extrabold text-emerald-800" data-testid={`total-${label}-${row.staff_name}`}>
                    {row.total_hours || 0}h
                  </td>
                ))}
              </tr>
              {/* NOTES row */}
              <tr className="border-t border-slate-200">
                <td className="py-2 px-3 font-semibold text-slate-600 text-xs sticky left-0 bg-white">Notes<br/><span className="text-[10px] font-normal text-slate-400">sick/annual leave</span></td>
                {staffRows.map(row => (
                  <td key={row.staff_name} className="py-2 px-1">
                    <Input value={row.week_notes || ''}
                      onChange={(e) => updateWeekNotes(row.staff_name, e.target.value)}
                      onBlur={() => saveWeekNotes(row.staff_name)}
                      placeholder="e.g. Sick Tue"
                      className="h-8 text-xs"
                      data-testid={`week-notes-${label}-${row.staff_name}`}/>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

export default function WeeklyRoster() {
  const { storeName, stores: bizStores } = useStore();
  const STORES = STORE_KEYS.filter(k => bizStores.length === 0 || bizStores.some(s => s.code === k)).map(k => ({ key: k, label: storeName(k) }));
  const { getAuthHeader, activeStaff } = useAuth();
  const [view, setView] = useState('fortnight'); // 'week' or 'fortnight'
  const [storeKey, setStoreKey] = useState(() => {
    try { return localStorage.getItem('roster_active_store') || 'orewa'; } catch { return 'orewa'; }
  });
  const [weekStart, setWeekStart] = useState(() => isoDate(getFortnightStart(new Date(), getStoreAnchor(localStorage.getItem('roster_active_store') || 'orewa'))));
  const [week1Rows, setWeek1Rows] = useState([]);
  const [week2Rows, setWeek2Rows] = useState([]);
  const [knownNames, setKnownNames] = useState([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  // Anchor + date picker draft (set-anchor row)
  const [anchorDraft, setAnchorDraft] = useState(() => getStoreAnchor(storeKey));

  const monday = new Date(weekStart + 'T00:00:00');
  const week2Start = isoDate(addDays(monday, 7));

  // When user switches stores, recompute weekStart from that store's anchor + remember choice
  const switchStore = (next) => {
    if (next === storeKey) return;
    setStoreKey(next);
    try { localStorage.setItem('roster_active_store', next); } catch (_e) { /* ignore */ }
    const nextAnchor = getStoreAnchor(next);
    setAnchorDraft(nextAnchor);
    setWeekStart(isoDate(getFortnightStart(new Date(), nextAnchor)));
  };

  // Save a new anchor for the current store and snap to its fortnight
  const applyAnchor = (newAnchorIso) => {
    if (!newAnchorIso || !/^\d{4}-\d{2}-\d{2}$/.test(newAnchorIso)) {
      toast.error('Pick a valid date');
      return;
    }
    // Force the chosen date to its Monday so anchors are always Mondays
    const monday = getMonday(new Date(newAnchorIso + 'T00:00:00'));
    const mondayIso = isoDate(monday);
    setStoreAnchor(storeKey, mondayIso);
    setAnchorDraft(mondayIso);
    setWeekStart(mondayIso);
    toast.success(`${STORES.find(s => s.key === storeKey)?.label} fortnight now anchored to ${monday.toLocaleDateString('en-NZ', { weekday: 'short', day: '2-digit', month: 'short' })}`);
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const storeQS = `&store=${encodeURIComponent(storeKey)}`;
      if (view === 'fortnight') {
        const [fnRes, namesRes] = await Promise.all([
          axios.get(`${API_URL}/staff/fortnight-hours?week_start=${weekStart}${storeQS}`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/staff/weekly-hours/known-staff?store=${encodeURIComponent(storeKey)}`, { headers: getAuthHeader() }),
        ]);
        // Split into week1 and week2 with the staff_name and week_notes
        const w1 = [], w2 = [];
        (fnRes.data.staff || []).forEach(p => {
          if (p.week1) w1.push(p.week1); else w1.push({ staff_name: p.staff_name, days: {}, total_hours: 0, week_notes: '' });
          if (p.week2) w2.push(p.week2); else w2.push({ staff_name: p.staff_name, days: {}, total_hours: 0, week_notes: '' });
        });
        // Backfill empty day objects
        const padDays = (s) => {
          const days = { ...s.days };
          for (let i = 0; i < 7; i++) {
            if (!days[i] && !days[String(i)]) days[i] = { start_time: '', end_time: '', break_minutes: 0, hours: 0 };
          }
          return { ...s, days, store: storeKey };
        };
        setWeek1Rows(w1.map(padDays));
        setWeek2Rows(w2.map(padDays));
        setKnownNames(namesRes.data?.names || []);
      } else {
        const [r, namesRes] = await Promise.all([
          axios.get(`${API_URL}/staff/weekly-hours?week_start=${weekStart}${storeQS}`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/staff/weekly-hours/known-staff?store=${encodeURIComponent(storeKey)}`, { headers: getAuthHeader() }),
        ]);
        setWeek1Rows((r.data?.staff || []).map(s => ({ ...s, store: storeKey })));
        setWeek2Rows([]);
        setKnownNames(namesRes.data?.names || []);
      }
    } catch (e) {
      toast.error('Failed to load roster');
    } finally {
      setLoading(false);
    }
  }, [view, weekStart, storeKey, getAuthHeader]);

  useEffect(() => { refresh(); }, [refresh]);

  const addStaff = async (name) => {
    const trimmed = (name || newName).trim();
    if (!trimmed) return;
    try {
      await axios.post(`${API_URL}/staff/weekly-hours/add-staff`, { staff_name: trimmed, week_start: weekStart, store: storeKey }, { headers: getAuthHeader() });
      if (view === 'fortnight') {
        await axios.post(`${API_URL}/staff/weekly-hours/add-staff`, { staff_name: trimmed, week_start: week2Start, store: storeKey }, { headers: getAuthHeader() });
      }
      refresh();
    } catch (e) { toast.error('Failed to add'); }
  };

  const shiftPeriod = (delta) => {
    const step = view === 'fortnight' ? 14 : 7;
    setWeekStart(isoDate(addDays(monday, delta * step)));
  };
  const goNow = () => {
    setWeekStart(isoDate(view === 'fortnight' ? getFortnightStart(new Date(), getStoreAnchor(storeKey)) : getMonday(new Date())));
  };
  // Keep weekStart aligned when toggling between week ↔ fortnight views
  const switchView = (next) => {
    if (next === view) return;
    if (next === 'fortnight') {
      setWeekStart(isoDate(getFortnightStart(new Date(weekStart + 'T00:00:00'), getStoreAnchor(storeKey))));
    }
    setView(next);
  };

  const fortnightTotal = Math.round(([...week1Rows, ...week2Rows].reduce((s, r) => s + (r.total_hours || 0), 0)) * 100) / 100;
  const week2Monday = addDays(monday, 7);
  const periodLabel = view === 'fortnight'
    ? `${monday.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' })} – ${addDays(monday, 13).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' })}`
    : `${monday.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' })} – ${addDays(monday, 6).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' })}`;

  return (
    <div className="space-y-4 max-w-[1100px]" data-testid="weekly-roster-page">
      <style>{`@media print { @page { size: A4 portrait; margin: 10mm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1b5e20]">Weekly Roster</h1>
          <p className="text-sm text-slate-500">Each store has its own roster &amp; payday. Pick the store, then type in shifts.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Store toggle — separate roster per store */}
          <div className="flex rounded-md border-2 border-[#1b5e20] overflow-hidden" data-testid="store-toggle">
            {STORES.map(s => (
              <button
                key={s.key}
                onClick={() => switchStore(s.key)}
                data-testid={`store-${s.key}`}
                className={`px-4 py-1.5 text-sm font-bold transition-colors ${storeKey === s.key ? 'bg-[#1b5e20] text-white' : 'bg-white text-[#1b5e20] hover:bg-emerald-50'}`}
              >{s.label}</button>
            ))}
          </div>
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            <button
              onClick={() => switchView('week')}
              data-testid="view-week"
              className={`px-3 py-1.5 text-sm font-medium ${view === 'week' ? 'bg-[#1b5e20] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
            >1 Week</button>
            <button
              onClick={() => switchView('fortnight')}
              data-testid="view-fortnight"
              className={`px-3 py-1.5 text-sm font-medium ${view === 'fortnight' ? 'bg-[#1b5e20] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
            >Fortnight (wages)</button>
          </div>
          <Button variant="outline" onClick={() => shiftPeriod(-1)} data-testid="prev-period"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={goNow} data-testid="this-period" className="font-semibold"><Calendar className="w-4 h-4 mr-2" /> Now</Button>
          <Button variant="outline" onClick={() => shiftPeriod(1)} data-testid="next-period"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-roster"><Printer className="w-4 h-4 mr-2" /> Print</Button>
        </div>
      </div>

      {/* Staff Away / Time Off — managed here on the roster */}
      <ShiftAlerts />

      {/* Add Staff — prominent at the TOP so staff don't have to scroll past
          the entire fortnight to find it (the old position at the bottom was
          getting missed, leading to "I can't add staff" reports). */}
      <Card data-testid="add-staff-top">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-[#1b5e20] shrink-0">
            <Plus className="w-4 h-4" /> Add staff to this period:
          </div>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { const v = newName; setNewName(''); addStaff(v); } }}
            placeholder="Type a name (or pick from the list)"
            list="known-staff-names"
            className="w-64 h-9" data-testid="new-staff-input-top"/>
          <datalist id="known-staff-names">
            {knownNames.map(n => <option key={n} value={n} />)}
          </datalist>
          <Button onClick={() => { const v = newName; setNewName(''); addStaff(v); }} data-testid="add-staff-btn-top" className="bg-[#1b5e20] hover:bg-[#164e1a] text-white">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
          {knownNames.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap text-xs ml-2">
              <span className="text-slate-400">Quick-add:</span>
              {knownNames
                .filter(n => !week1Rows.some(r => r.staff_name === n) && !week2Rows.some(r => r.staff_name === n))
                .slice(0, 8)
                .map(n => (
                  <button key={n} onClick={() => addStaff(n)}
                    className="px-2 py-1 rounded-full border border-slate-300 hover:border-[#1b5e20] hover:text-[#1b5e20] transition-colors"
                    data-testid={`quick-add-top-${n}`}>+ {n}</button>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Date picker — pick any date; we'll snap to the Monday of that week and use it as the anchor */}
      {view === 'fortnight' && (
        <div className="flex flex-wrap items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
          <div className="text-xs text-emerald-900 font-semibold uppercase">
            {STORES.find(s => s.key === storeKey)?.label} fortnight anchor
          </div>
          <Input
            type="date"
            value={anchorDraft}
            onChange={(e) => setAnchorDraft(e.target.value)}
            className="h-8 text-sm w-[160px]"
            data-testid="anchor-date-input"
          />
          <Button
            size="sm"
            onClick={() => applyAnchor(anchorDraft)}
            className="bg-[#1b5e20] hover:bg-[#164e1a] h-8"
            data-testid="apply-anchor-btn"
          >Set as fortnight start</Button>
          <span className="text-[11px] text-emerald-800">
            ✓ Current: {getStoreAnchor(storeKey)} — change this if {STORES.find(s => s.key === storeKey)?.label}&apos;s pay cycle starts on a different Monday.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between bg-slate-50 rounded-lg border border-slate-200 px-4 py-3 flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase text-slate-500 flex items-center gap-2">
            <Badge className="bg-[#1b5e20] text-white" data-testid="active-store-badge">{STORES.find(s => s.key === storeKey)?.label}</Badge>
            {view === 'fortnight' ? 'Pay period (2 weeks · Mon → Sun)' : 'Pay period (1 week · Mon → Sun)'}
          </p>
          <p className="text-lg font-bold text-slate-800" data-testid="period-label">{periodLabel}</p>
          {view === 'fortnight' && (
            <p className="text-xs text-amber-700 font-semibold mt-1" data-testid="payday-label">
              💰 Payday: {fmtFullDate(addDays(monday, 18))}
            </p>
          )}
        </div>
        {view === 'fortnight' && (
          <div className="text-right">
            <p className="text-xs uppercase text-emerald-700">Fortnight Total</p>
            <p className="text-3xl font-extrabold text-emerald-700" data-testid="fortnight-total">{fortnightTotal} hrs</p>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-center text-slate-500 py-8">Loading...</p>
      ) : (
        <>
          <WeekTable
            weekStart={weekStart}
            staffRows={week1Rows}
            setStaffRows={setWeek1Rows}
            knownNames={knownNames}
            refresh={refresh}
            getAuthHeader={getAuthHeader}
            label={view === 'fortnight' ? '1' : 'week'}
            fortnightView={view === 'fortnight'}
          />
          {view === 'fortnight' && (
            <WeekTable
              weekStart={week2Start}
              staffRows={week2Rows}
              setStaffRows={setWeek2Rows}
              knownNames={knownNames}
              refresh={refresh}
              getAuthHeader={getAuthHeader}
              label="2"
              fortnightView={true}
            />
          )}
        </>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-slate-500">
            <Pencil className="w-4 h-4" /> Renaming &amp; rename history
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[12px] text-slate-500">
            💡 Need to rename someone (e.g. you typed &quot;Tina&quot; but it should be &quot;Katie&quot;)? Click the <Pencil className="w-3 h-3 inline align-middle" /> pencil icon next to their name in the table — it preserves all their hours &amp; history. To add a new person, use the <strong>Add staff</strong> bar at the top of this page.
          </p>
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400">
        💡 Fortnight view is best for wages. Each cell shows hours worked (green badge) — total of work hours minus break.
        Use the Notes column to log sick leave / annual leave for the week. {activeStaff?.name && <>Signed in as <strong>{activeStaff.name}</strong>.</>}
      </p>
    </div>
  );
}
